import React, { useState, useEffect } from 'react';
import { Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    ADMIN_VIEWS,
    SAVED_SELECTED_MENU_STORAGE_KEY,
    VIEW_TO_ROUTE,
    ROUTE_TO_VIEW,
} from '../../utils/constant';
import { AdminDashboardView } from './AdminDashboardView';
import AdminGlobalMenu from './AdminGlobalMenu';
import AdminProjectsTab from './projects/AdminProjectsTab';
import AdminGroupsTab from './groups/AdminGroupsTab';
import NewProjectForm from './projects/NewProjectForm';
import NewGroupForm from './groups/NewGroupForm';
import EditGroupForm from './groups/EditGroupForm';
import GlobalUserListTable from './users/GlobalUserListTable';
import NewUserForm from './users/NewUserForm';
import NewCashInForm from './NewCashInForm';
import NewCashOutForm from './NewCashOutForm';
import AdminTransactionHistory from './AdminTransactionHistory';
import AdminMessagingView from './messaging/AdminMessagingView';
import { AdminProjectProvider } from './projects/AdminProjectProvider';
import { AdminUserContext } from '../../contexts/AdminUserContext';
import { ParticipantData } from '../../interface/Participant.interface';
import { useUsers } from '../../api/users/useUsers';
import '../../styles/AdminHome.css';


export const AdminHome: React.FC = () => {
    const [currentView, setCurrentView] = useState(ADMIN_VIEWS.DASHBOARD_VIEW);
    const [selectedProjectParticipant, setSelectedProjectParticipant] = useState<ParticipantData | undefined>(undefined);
    const [groupToEdit, setGroupToEdit] = useState<any>(null);
    const navigate = useNavigate();

    // Use React Query hook for users - automatic caching and deduplication
    const { data: users = [] } = useUsers();

    const userContextValue = {
        users,
        updateUsers: () => {}, // No-op: React Query handles updates via invalidation
        selectedProjectParticipant,
        updateSelectedProjectParticipant: setSelectedProjectParticipant,
    };

    const changeView = (newView: number) => {
        setCurrentView(newView);
        localStorage.setItem(SAVED_SELECTED_MENU_STORAGE_KEY, newView.toString());
        const routeToNavigate = VIEW_TO_ROUTE[newView];
        if (routeToNavigate) navigate(routeToNavigate);
    };

    const handleEditGroup = (group: any) => {
        setGroupToEdit(group);
        changeView(ADMIN_VIEWS.EDIT_GROUP_VIEW);
    };

    const renderContent = () => {
        switch (currentView) {
            case ADMIN_VIEWS.DASHBOARD_VIEW: return <AdminDashboardView selectedMenu={changeView} />;
            case ADMIN_VIEWS.PROJECT_LIST_VIEW: return <AdminProjectsTab selectedMenu={changeView} />;
            case ADMIN_VIEWS.USER_LIST_VIEW: return <GlobalUserListTable selectedMenu={changeView} />;
            case ADMIN_VIEWS.NEW_PROJECT_VIEW: return <NewProjectForm selectedMenu={changeView} />;
            case ADMIN_VIEWS.NEW_USER_VIEW: return <NewUserForm selectedMenu={changeView} />;
            case ADMIN_VIEWS.NEW_CASH_IN_VIEW: return <NewCashInForm selectedMenu={changeView} />;
            case ADMIN_VIEWS.NEW_CASH_OUT_VIEW: return <NewCashOutForm selectedMenu={changeView} />;
            case ADMIN_VIEWS.TRANSACTION_HISTORY_VIEW: return <AdminTransactionHistory selectedMenu={changeView} />;
            case ADMIN_VIEWS.GROUP_LIST_VIEW: return <AdminGroupsTab selectedMenu={changeView} onEditGroup={handleEditGroup} />;
            case ADMIN_VIEWS.NEW_GROUP_VIEW: return <NewGroupForm selectedMenu={changeView} />;
            case ADMIN_VIEWS.EDIT_GROUP_VIEW:
                return groupToEdit ? (
                    <EditGroupForm
                        selectedMenu={changeView}
                        groupToEdit={groupToEdit}
                        onUpdate={() => {
                            setGroupToEdit(null);
                            changeView(ADMIN_VIEWS.GROUP_LIST_VIEW);
                        }}
                    />
                ) : null;
            case ADMIN_VIEWS.MESSAGING_VIEW:
                return <AdminMessagingView />;
            default: return <AdminDashboardView selectedMenu={changeView} />;
        }
    };

    // Restore view from route or localStorage
    useEffect(() => {
        const currentRoute = location.pathname;
        const viewFromRoute = ROUTE_TO_VIEW[currentRoute];
        if (viewFromRoute !== undefined) setCurrentView(viewFromRoute);
        else {
            const savedView = localStorage.getItem(SAVED_SELECTED_MENU_STORAGE_KEY);
            setCurrentView(savedView ? parseInt(savedView) : ADMIN_VIEWS.DASHBOARD_VIEW);
        }
    }, [location.pathname]);

    return (
        <AdminProjectProvider>
            <AdminUserContext.Provider value={userContextValue}>
                <div className="admin-container">
                    <div className="admin-content-wrapper">
                        <AdminGlobalMenu selectedMenu={changeView} />
                        <div className="admin-main-content">
                            <Fade in timeout={500}>
                                <div className="admin-view-container">{renderContent()}</div>
                            </Fade>
                        </div>
                    </div>
                </div>
            </AdminUserContext.Provider>
        </AdminProjectProvider>
    );
};
